import * as React from "react";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import styled from "styled-components";

import { color } from "../tokens/colors";

const UncheckedRadio = styled("span")({
  borderRadius: "50%",
  width: "1rem",
  height: "1rem",
  boxShadow:
    "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
  backgroundColor: color.neutral[200],
});

const CheckedRadio = styled(UncheckedRadio)({
  "&::before": {
    display: "block",
    width: "1rem",
    height: "1rem",
    backgroundImage: `radial-gradient(${color.neutral[500]}, ${color.neutral[500]} 30%,transparent 40%)`,
    content: '""',
  },

  "input:hover ~ &::before": {
    backgroundImage: `radial-gradient(${color.blue[300]}, ${color.blue[300]} 30%,transparent 40%)`,
  },
});

interface Props {
  children: React.ReactNode;
}

export const Init: React.FC<Props> = (props) => {
  const { children } = props;

  const theme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: color.blue[300],
      },
      secondary: {
        main: color.neutral[400],
      },
      error: {
        main: color.red[300],
      },
      warning: {
        main: color.yellow[300],
      },
      info: {
        main: color.blue[300],
      },
      success: {
        main: color.green[300],
      },
      background: {
        default: color.background.default,
        paper: color.basic.white,
      },
      text: {
        primary: color.foreground.default,
        secondary: color.foreground.muted,
        disabled: color.foreground.disabled,
      },
    },
    shape: {
      borderRadius: "0.5rem",
    },
    spacing: "0.5rem",
    typography: {
      fontFamily:
        '"InterVariable", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            "&:not(:last-child)": {
              borderBottom: 0,
            },
            "&::before": {
              display: "none",
            },
            border: `1px solid ${color.border.default}`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "none",
            },
            borderRadius: "0.25rem",

            "&.Mui-disabled": {
              cursor: "not-allowed",
              pointerEvents: "auto",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
            },
          },
          startIcon: {
            "& > *:nth-of-type(1)": {
              fontSize: "inherit",
            },
          },
          endIcon: {
            "& > *:nth-of-type(1)": {
              fontSize: "inherit",
            },
          },
          textPrimary: {
            "&:hover": {
              backgroundColor: color.blue[100],
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            "&.Mui-disabled": {
              cursor: "not-allowed",
              pointerEvents: "auto",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
            },
          },
          colorPrimary: {
            color: color.blue[300],
            "&:hover": {
              backgroundColor: color.blue[100],
            },
          },
          colorSecondary: {
            color: color.neutral[400],
            "&:hover": {
              backgroundColor: color.neutral[200],
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: color.neutral[500],
            fontSize: "0.75rem",
            lineHeight: "1rem",
            padding: "0.75rem",
            borderRadius: "0.5rem",
          },
          arrow: {
            color: color.neutral[500],
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            ".MuiTabs-scrollButtons.Mui-disabled": {
              opacity: 0.2,
            },
          },
          indicator: {
            backgroundColor: color.neutral[500],
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              color: color.neutral[500],
            },
          },
        },
      },
      MuiRadio: {
        defaultProps: {
          checkedIcon: <CheckedRadio />,
          icon: <UncheckedRadio />,
        },
        styleOverrides: {
          root: {},
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: "0.875rem", // 14px
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            // bodyLarge
            fontFamily:
              '"InterVariable", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
            fontSize: "1rem",
            fontWeight: 400,
            lineHeight: "1.5rem",
            letterSpacing: "-0.013rem",
            color: color.foreground.default,
            padding: "1rem 0",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            "& .MuiDialog-paper": {
              padding: "2rem",
              position: "relative",
            },
            "& .MuiDialog-paperWidthMd": {
              width: "50rem",
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "2rem 0 0 0",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: color.neutral[300],
          },
        },
      },
    },
  });

  return (
    <>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </>
  );
};
